import React from "react";

const TERMS_MARKDOWN_URL = "/tou.md";
const DRAFTING_NOTES_PATTERN =
  /\n---\n\n# Drafting Notes \/ Items to Confirm Before Publishing[\s\S]*$/;

function getPublicTerms(markdown: string) {
  return markdown.replace(DRAFTING_NOTES_PATTERN, "").trim();
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const tokenPattern = /(\*\*.+?\*\*|\[[^\]]+?\]\([^)]+?\))/g;
  let lastIndex = 0;
  let tokenIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${tokenIndex}`;

    if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else {
      const link = /^\[([^\]]+?)\]\(([^)]+?)\)$/.exec(token);
      if (link) {
        const href = link[2];
        const isExternal = /^https?:\/\//.test(href);
        nodes.push(
          <a
            href={href}
            key={key}
            rel={isExternal ? "noopener noreferrer" : undefined}
            target={isExternal ? "_blank" : undefined}
          >
            {link[1]}
          </a>,
        );
      } else {
        nodes.push(token);
      }
    }

    lastIndex = tokenPattern.lastIndex;
    tokenIndex += 1;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length ? nodes : [text];
}

function renderParagraph(lines: string[], key: string) {
  const content: React.ReactNode[] = [];

  lines.forEach((line, index) => {
    content.push(...renderInline(line.trimEnd(), `${key}-${index}`));
    if (index < lines.length - 1) {
      content.push(<br key={`${key}-br-${index}`} />);
    }
  });

  return <p key={key}>{content}</p>;
}

function renderMarkdown(markdown: string) {
  const blocks: React.ReactElement[] = [];
  const paragraph: string[] = [];
  let list:
    | {
        ordered: boolean;
        items: string[];
      }
    | null = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push(renderParagraph(paragraph, `p-${blocks.length}`));
    paragraph.length = 0;
  };

  const flushList = () => {
    if (!list) return;

    const items = list.items.map((item, index) => (
      <li key={index}>{renderInline(item, `li-${blocks.length}-${index}`)}</li>
    ));

    blocks.push(
      list.ordered ? (
        <ol key={`ol-${blocks.length}`}>{items}</ol>
      ) : (
        <ul key={`ul-${blocks.length}`}>{items}</ul>
      ),
    );
    list = null;
  };

  markdown
    .replace(/\r\n/g, "\n")
    .split("\n")
    .forEach((line) => {
      const trimmed = line.trim();

      if (!trimmed) {
        flushParagraph();
        flushList();
        return;
      }

      if (trimmed === "---") {
        flushParagraph();
        flushList();
        blocks.push(<hr key={`hr-${blocks.length}`} />);
        return;
      }

      const heading = /^(#{1,3})\s+(.+)$/.exec(trimmed);
      if (heading) {
        flushParagraph();
        flushList();
        const content = renderInline(heading[2], `h-${blocks.length}`);

        if (heading[1].length === 1) {
          blocks.push(<h1 key={`h1-${blocks.length}`}>{content}</h1>);
        } else if (heading[1].length === 2) {
          blocks.push(<h2 key={`h2-${blocks.length}`}>{content}</h2>);
        } else {
          blocks.push(<h3 key={`h3-${blocks.length}`}>{content}</h3>);
        }
        return;
      }

      const unorderedItem = /^-\s+(.+)$/.exec(trimmed);
      const orderedItem = /^\d+\.\s+(.+)$/.exec(trimmed);
      const listItem = unorderedItem || orderedItem;

      if (listItem) {
        flushParagraph();
        const ordered = Boolean(orderedItem);
        if (!list || list.ordered !== ordered) {
          flushList();
          list = { ordered, items: [] };
        }
        list.items.push(listItem[1]);
        return;
      }

      flushList();
      paragraph.push(line);
    });

  flushParagraph();
  flushList();

  return blocks;
}

export default function Terms() {
  const [markdown, setMarkdown] = React.useState<string | null>(null);
  const [hasError, setHasError] = React.useState(false);

  React.useEffect(() => {
    document.title = "Terms of Use | Evo";

    let cancelled = false;

    fetch(`${TERMS_MARKDOWN_URL}?v=${Date.now()}`, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error("Terms file not found");
        return response.text();
      })
      .then((text) => {
        const trimmedText = text.trimStart().toLowerCase();
        if (
          trimmedText.startsWith("<!doctype html") ||
          trimmedText.startsWith("<html")
        ) {
          throw new Error("Terms markdown resolved to HTML");
        }
        if (!cancelled) setMarkdown(text);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#faf9fb] text-[#07001a]">
      <header className="mx-auto flex w-full max-w-[960px] items-center justify-between px-6 py-5">
        <a
          className="font-['Figtree',sans-serif] text-[20px] font-black text-[#07001a] no-underline"
          href="/"
        >
          evo
        </a>
        <a
          className="font-['Figtree',sans-serif] text-[14px] font-semibold text-[#776f7b] no-underline hover:text-[#07001a]"
          href="/privacy"
        >
          Privacy
        </a>
      </header>

      <main className="mx-auto w-full max-w-[840px] px-6 pb-16 pt-8">
        {hasError ? (
          <p className="font-['Figtree',sans-serif] text-[15px] text-[#776f7b]">
            Terms of Use could not be loaded.
          </p>
        ) : markdown ? (
          <article className="terms-content">
            {renderMarkdown(getPublicTerms(markdown))}
          </article>
        ) : (
          <p className="font-['Figtree',sans-serif] text-[15px] text-[#776f7b]">
            Loading Terms of Use...
          </p>
        )}
      </main>
    </div>
  );
}
